import { useEffect, RefObject } from "react";

/**
 * Custom hook to manage the --banner-height CSS variable
 *
 * This hook centralizes all banner height management logic and eliminates
 * conflicts from multiple components trying to manage the same CSS variable.
 *
 * @param bannerRef - Ref to the banner element to measure
 * @param isDesktop - Whether the viewport is desktop size (≥992px)
 *
 * Behavior:
 * - On desktop: Sets --banner-height to the actual banner element height in pixels
 * - On mobile/tablet: Sets --banner-height to 0px (banner is in-flow, not sticky)
 * - Automatically updates on resize via ResizeObserver
 * - Cleans up CSS variable on unmount
 */
const useBannerHeight = (
  bannerRef: RefObject<HTMLElement | null> | null,
  isDesktop: boolean
): void => {
  useEffect(() => {
    const rootStyle = document.documentElement.style;

    // Mobile/tablet: banner is not sticky, set height to 0
    if (!isDesktop) {
      rootStyle.setProperty("--banner-height", "0px");
      return () => {
        rootStyle.removeProperty("--banner-height");
      };
    }

    // Desktop: measure and track actual banner height
    const updateBannerHeight = () => {
      const height = bannerRef?.current?.offsetHeight ?? 0;
      if (height > 0) {
        rootStyle.setProperty("--banner-height", `${height}px`);
      } else {
        rootStyle.removeProperty("--banner-height");
      }
    };

    updateBannerHeight();

    // Set up ResizeObserver to track height changes
    let resizeObserver: ResizeObserver | null = null;

    if (typeof ResizeObserver !== "undefined" && bannerRef?.current) {
      resizeObserver = new ResizeObserver(() => {
        updateBannerHeight();
      });
      resizeObserver.observe(bannerRef.current);
    } else {
      // Fallback for browsers without ResizeObserver
      const handleResize = () => updateBannerHeight();
      window.addEventListener("resize", handleResize);

      const timers = [
        window.setTimeout(updateBannerHeight, 0),
        window.setTimeout(updateBannerHeight, 200)
      ];

      return () => {
        timers.forEach((timer) => window.clearTimeout(timer));
        window.removeEventListener("resize", handleResize);
        rootStyle.removeProperty("--banner-height");
      };
    }

    return () => {
      resizeObserver?.disconnect();
      rootStyle.removeProperty("--banner-height");
    };
  }, [bannerRef, isDesktop]);
};

export default useBannerHeight;